import { type ActorRefFromLogic, type SnapshotFrom } from "xstate";

import {
	GIT_PUBLISHING_BRANCH_NAME,
	GIT_PUBLISHING_REMOTE_NAME,
} from "~/state/machines/git/addGitMomentRemote";
import type { BranchInfo as GitBranchInfo } from "~/state/machines/git/gitBranches.machine";
import type {
	GitRemote,
	GitRepoStatus,
} from "~/state/machines/git/repository/git-repository.types";
import { getDocsApiUrl } from "~/utils/docsapi";

import { type AuthenticationMachine } from "../authentication";

export type PublishStatus = "status-pending" | "published" | "unpublished";
export type SyncMode = "publish-if-unpublished" | "try-sync";

export type PlanStatus =
	| "status-pending"
	| "unachievable-nothing-to-do"
	| "unachievable-not-on-synced-branch"
	| "unachievable-not-authenticated"
	| "unachievable-unauthorized"
	| "unachievable-behind-remote"
	| "achievable";

const actions = [
	"create-publish-repo",
	"initialize-git-repo",
	"commit-initial-changes",
	"commit-outstanding-changes",
	"create-remote",
	"set-tracking-branch",
	"push-to-remote",
	"publish-document",
	"revalidate-document",
] as const;

export type PlannedOutcome = null | "commit" | "push" | "publish";

export type SyncAction = (typeof actions)[number];

export function actionIsPlanned(plan: SyncPlan | undefined | null, action: SyncAction): boolean {
	return plan?.actions.includes(action) ?? false;
}

export const actionsOrder: { [key in SyncAction]: number } = {
	"create-publish-repo": 0,
	"initialize-git-repo": 1,
	"commit-initial-changes": 2,
	"commit-outstanding-changes": 3,
	"create-remote": 4,
	"set-tracking-branch": 5,
	"push-to-remote": 6,
	"publish-document": 7,
	"revalidate-document": 8,
} as const;

export interface SyncPlan {
	planStatus: PlanStatus;
	publishStatus: PublishStatus;
	actions: SyncAction[];
	plannedOutcome: PlannedOutcome;
	branch: string | undefined;
	ahead: number;
	behind: number;
}

export const emptySyncPlan = (): SyncPlan => ({
	planStatus: "status-pending",
	publishStatus: "status-pending",
	actions: [],
	plannedOutcome: null,
	branch: undefined,
	ahead: 0,
	behind: 0,
});

// TODO: This is clearly not the best pattern for the
// ternary boolean in Typescript :(
export type SyncPlanMaybe<T> = T | "unknown";

export function syncPlan(args: {
	syncMode: SyncMode;

	authenticationSnap: SnapshotFrom<ActorRefFromLogic<AuthenticationMachine>>;

	gitRepoStatus: SyncPlanMaybe<GitRepoStatus | undefined>;
	trackingBranches: SyncPlanMaybe<GitBranchInfo[]>;
	remotes: SyncPlanMaybe<GitRemote[]>;

	remoteRepoExists: SyncPlanMaybe<boolean>;
	hasPublishPermission: SyncPlanMaybe<boolean>;
	isPublished: SyncPlanMaybe<boolean>;
}): SyncPlan {
	const {
		syncMode,
		authenticationSnap,

		gitRepoStatus,
		trackingBranches,
		remotes,

		remoteRepoExists,
		hasPublishPermission,
		isPublished,
	} = args;
	// if any git statuses fail, immediately return the empty plan
	if (gitRepoStatus === "unknown" || trackingBranches === "unknown" || remotes === "unknown") {
		return emptySyncPlan();
	}

	const { authnStatus } = authenticationSnap.context;
	const isAuthenticated = authnStatus === "authenticated";

	// note that if the repository is uninitialized, then we are defacto on
	// the publishing branch (because it will be initialized into main). however
	// this shouldn't happen because the repo should be init'd during document load.
	const isOnPublishBranch =
		(gitRepoStatus && gitRepoStatus.branch === GIT_PUBLISHING_BRANCH_NAME) || !gitRepoStatus;
	const documentRemoteSet =
		remotes?.find(
			(remote) =>
				remote.name === GIT_PUBLISHING_REMOTE_NAME && remote.url.startsWith(getDocsApiUrl())
		) !== undefined;
	const trackingBranchSet = !!trackingBranches?.find(
		(branch) => branch.name === GIT_PUBLISHING_BRANCH_NAME
	)?.upstream;

	const branch = gitRepoStatus && gitRepoStatus?.branch ? gitRepoStatus.branch : undefined;
	const ahead = gitRepoStatus && gitRepoStatus?.ahead ? gitRepoStatus?.ahead : 0;
	const behind = gitRepoStatus && gitRepoStatus?.behind ? gitRepoStatus?.behind : 0;
	const repoClean = gitRepoStatus && gitRepoStatus?.isClean;
	const repoAhead = ahead > 0;

	const [actions, plannedOutcome] = (function () {
		const actions = new Set<SyncAction>();
		let plannedOutcome: PlannedOutcome = null;

		const shouldAutoPublish = syncMode === "publish-if-unpublished" && isOnPublishBranch;
		const shouldAutoPush =
			syncMode === "try-sync" && isOnPublishBranch && remoteRepoExists === true;

		//
		// Attempt to add the actions to the action set in roughly the order they'd be executed.
		//

		if (shouldAutoPublish && remoteRepoExists === false) {
			actions.add("create-publish-repo");
		}

		// first evaluate local sync actions (e.g. commit)

		if (!gitRepoStatus) {
			actions.add("initialize-git-repo");
			actions.add("commit-initial-changes");
			plannedOutcome = "commit";
		}

		if (!repoClean) {
			actions.add("commit-outstanding-changes");
			plannedOutcome = "commit";
		}

		// next, should we attempt to go upstream?
		// within this block, we are on the publishing branch (or what will be come it)
		if (shouldAutoPush || shouldAutoPublish) {
			if (!gitRepoStatus || !documentRemoteSet) {
				actions.add("create-remote");
			}
			if (!gitRepoStatus || !documentRemoteSet || !trackingBranchSet) {
				actions.add("set-tracking-branch");
			}

			if (
				// These 3 represent that we initialized the upstream new, so we have to push
				!gitRepoStatus ||
				!documentRemoteSet ||
				!trackingBranchSet ||
				// these 2 states represent that we have changes to send upstream
				!repoClean ||
				repoAhead
			) {
				actions.add("push-to-remote");
				plannedOutcome = "push";
			}
		}

		if (shouldAutoPublish && (remoteRepoExists === false || isPublished === false)) {
			actions.add("publish-document");
			plannedOutcome = "publish"; // This plan results in a publish.
		}

		return [
			[...actions].sort((a, b) => actionsOrder[a] - actionsOrder[b]),
			plannedOutcome,
		] as const;
	})();

	let planStatus: PlanStatus;
	if (!isAuthenticated) {
		planStatus = "unachievable-not-authenticated";
	} else if (gitRepoStatus && !isOnPublishBranch) {
		planStatus = "unachievable-not-on-synced-branch";
	} else if (remoteRepoExists === true && !(hasPublishPermission === true)) {
		// TODO: check for workspace push permissions if the repository does not exist upstream,
		// which is defined as workspace ownership permissions today.
		planStatus = "unachievable-unauthorized";
	} else if (gitRepoStatus && gitRepoStatus.behind > 0) {
		planStatus = "unachievable-behind-remote";
	} else if (remoteRepoExists === true && isPublished === "unknown") {
		planStatus = "status-pending";
	} else if (actions.length === 0) {
		planStatus = "unachievable-nothing-to-do";
	} else {
		planStatus = "achievable";
	}

	let publishStatus: PublishStatus;
	if (remoteRepoExists === false) {
		publishStatus = "unpublished";
	} else if (remoteRepoExists === true && isPublished === false) {
		publishStatus = "unpublished";
	} else if (remoteRepoExists === true && isPublished === true) {
		publishStatus = "published";
	} else {
		publishStatus = "status-pending";
	}

	return {
		planStatus,
		publishStatus,
		actions,
		plannedOutcome,
		branch,
		ahead,
		behind,
	};
}
